/**
 * Authored atmosphere moods (owner: world-artist). A mood bundles lighting, fog, grade, how lit the
 * city's lamps and windows are, and the weather. Render, world view and fx subscribe. Which mood shows
 * is decided below: Settings → Time of day (AUTO runs a borough clock that cross-fades day → dusk →
 * night; DAY / DUSK / NIGHT hold one), persisted locally, and a mission may force one for its duration.
 */
import { CLOCK_HOURS_PER_SECOND, CLOCK_START_HOUR, type SkyWeather } from '../../shared/clock';
export type MoodId = 'dusk' | 'overcast' | 'night';
export type WeatherKind = SkyWeather;

export interface Mood {
  id: MoodId;
  label: string;
  sun: { color: number; intensity: number; dir: [number, number, number] };
  hemi: { sky: number; ground: number; intensity: number };
  rim: number;
  fog: { color: number; density: number };
  exposure: number;
  envIntensity: number;
  /** Multiplier on lit-window emissive. */
  windows: number;
  /** Multiplier on street-lamp glow and lamp pools on the road. */
  lamps: number;
  weather: WeatherKind;
  grade: { saturation: number; contrast: number; shadowTint: [number, number, number]; highTint: [number, number, number] };
  bloom: number;
  /**
   * Bloom spread (pmndrs mip blur radius, 0..1) and HDR luminance threshold (postPmndrs.ts). Night lowers
   * the threshold a little and widens the halo so lamps, headlamps, signs and lit windows glow softly.
   */
  bloomRadius: number;
  bloomThreshold: number;
  /** Sky gradient for the PMREM environment that lights and reflects off wet surfaces (linear RGB). */
  sky: { zenith: [number, number, number]; mid: [number, number, number]; horizon: [number, number, number]; ground: [number, number, number]; glow: [number, number, number] };
  /** Low river mist over the canal and basin. */
  haze: { color: number; opacity: number };
  /**
   * The sky behind the skyline (world/view/skyDome.ts): the horizon is always the fog colour; above it the
   * sky grades to `zenith`, and a glow band low over the horizon (`glow` at the bottom, `glowHigh` above
   * it) sits toward `glowDir` (xz heading): the last light at dusk, the town's sodium haze at night.
   * `glowSpread` 0 = the band all round, 1 = toward glowDir only; `glowFocus` how tight; `cloud` the
   * strength of the cloud mottling (0 = none).
   */
  dome: { zenith: number; glow: number; glowHigh: number; glowLevel: number; glowFocus: number; glowSpread: number; glowDir: [number, number]; cloud: number };
}

export const MOODS: Record<MoodId, Mood> = {
  dusk: {
    id: 'dusk',
    label: 'Drizzly dusk',
    sun: { color: 0xffb98a, intensity: 2.3, dir: [-0.72, 0.5, 0.48] },
    hemi: { sky: 0x8394bd, ground: 0x3b3128, intensity: 2.1 },
    rim: 0.6,
    fog: { color: 0x232a36, density: 0.0026 },
    exposure: 1.2,
    envIntensity: 0.8,
    // 1 burnt the lit panes to a white core inside the orange (docs/ART-DIRECTION.md)
    windows: 0.9,
    lamps: 1,
    weather: 'drizzle',
    grade: { saturation: 0.9, contrast: 1.04, shadowTint: [0.88, 0.97, 1.1], highTint: [1.08, 1.0, 0.88] },
    bloom: 0.45,
    bloomRadius: 0.6,
    bloomThreshold: 1.0,
    sky: { zenith: [0.05, 0.07, 0.13], mid: [0.16, 0.19, 0.28], horizon: [0.85, 0.45, 0.24], ground: [0.05, 0.045, 0.04], glow: [0.6, 0.28, 0.1] },
    haze: { color: 0x4a4e58, opacity: 0.3 },
    // sodium-orange last light low in the west-south-west, violet over it, a dark blue sky above
    dome: { zenith: 0x151a28, glow: 0xd87a2c, glowHigh: 0x5a3f6e, glowLevel: 0.4, glowFocus: 4, glowSpread: 0.85, glowDir: [-0.72, 0.48], cloud: 0.07 },
  },
  overcast: {
    // Warm-grey British day: a bright, high cloud deck with the sun just about showing through.
    id: 'overcast',
    label: 'Overcast day',
    sun: { color: 0xfff0dc, intensity: 2.5, dir: [-0.45, 0.8, 0.4] },
    hemi: { sky: 0xd9d6cf, ground: 0x6e655a, intensity: 2.5 },
    rim: 0.3,
    fog: { color: 0xa9a69e, density: 0.0019 },
    exposure: 1.05,
    envIntensity: 0.75,
    windows: 0.16,
    lamps: 0.06,
    weather: 'clear',
    grade: { saturation: 0.88, contrast: 1.08, shadowTint: [0.95, 0.98, 1.04], highTint: [1.05, 1.0, 0.92] },
    bloom: 0.22,
    bloomRadius: 0.6,
    bloomThreshold: 1.0,
    sky: { zenith: [0.34, 0.355, 0.37], mid: [0.42, 0.42, 0.41], horizon: [0.5, 0.48, 0.44], ground: [0.16, 0.145, 0.13], glow: [0.08, 0.065, 0.045] },
    haze: { color: 0xc4c2bb, opacity: 0.14 },
    // hazy grey-blue overhead, lighter at the horizon, the sun a faint warm brightening through the deck
    dome: { zenith: 0x8e949c, glow: 0xfff0dc, glowHigh: 0xe8e2d4, glowLevel: 0.1, glowFocus: 3, glowSpread: 1, glowDir: [-0.45, 0.4], cloud: 0.12 },
  },
  night: {
    // Moody but readable: cold moonlit fill, sodium and mercury pools, every lit window counts.
    id: 'night',
    label: 'Night',
    // Shapes come from the moon and a cool sky fill, not from exposure: a stronger moon (and rim) gives
    // roofs, bonnets and car tops a lit top face against their sides, and the lower exposure keeps lamp
    // pools and windows from blowing out around them.
    sun: { color: 0x9fb2dc, intensity: 1.2, dir: [0.35, 0.78, -0.5] },
    // Art-direction pass (docs/ART-DIRECTION.md): the sky fill a touch stronger and the fog a step lighter so
    // the road, pavement and roofs sit on three values instead of one navy; the grade's contrast eased and its
    // shadow tint less blue (over 60 % of a street shot sat under 32/255 on a phone); the lit windows below the
    // white-core burn and the bloom tighter so lamps still glow but the pub's windows no longer flare.
    hemi: { sky: 0x5a6b94, ground: 0x30281f, intensity: 2.75 },
    rim: 0.62,
    fog: { color: 0x161c2a, density: 0.0026 },
    exposure: 1.32,
    envIntensity: 0.8,
    windows: 0.8,
    lamps: 1.1,
    weather: 'clear',
    grade: { saturation: 0.86, contrast: 1.03, shadowTint: [0.9, 0.97, 1.12], highTint: [1.1, 1.0, 0.86] },
    bloom: 0.5,
    bloomRadius: 0.8,
    bloomThreshold: 0.9,
    sky: { zenith: [0.02, 0.026, 0.05], mid: [0.06, 0.075, 0.12], horizon: [0.2, 0.14, 0.1], ground: [0.025, 0.022, 0.02], glow: [0.2, 0.1, 0.035] },
    haze: { color: 0x2c3240, opacity: 0.32 },
    // the town's light pollution: a dim sodium glow round the horizon, strongest toward the centre (west)
    dome: { zenith: 0x070910, glow: 0xb86a28, glowHigh: 0x241a2a, glowLevel: 0.16, glowFocus: 2, glowSpread: 0.5, glowDir: [-0.72, 0.48], cloud: 0.04 },
  },
};

// ----------------------------------------------------------------------------- time of day

/**
 * What the player picked in Settings → Time of day. 'auto' runs the borough clock (one game hour per
 * 90 s of play, a 36-minute day) through day, dusk and night with slow cross-fades; the others hold one mood. A mission
 * may force a mood for its duration (`forceMood`), on top of either.
 */
export type TimeOfDay = 'auto' | 'day' | 'dusk' | 'night';
export const TIMES_OF_DAY: readonly { id: TimeOfDay; label: string }[] = [
  { id: 'auto', label: 'Auto (borough clock)' },
  { id: 'day', label: 'Day' },
  { id: 'dusk', label: 'Dusk' },
  { id: 'night', label: 'Night' },
];
const TOD_MOOD: Record<Exclude<TimeOfDay, 'auto'>, MoodId> = { day: 'overcast', dusk: 'dusk', night: 'night' };
const MOOD_TOD: Record<MoodId, Exclude<TimeOfDay, 'auto'>> = { overcast: 'day', dusk: 'dusk', night: 'night' };

/** Borough clock: game hours per second (an hour is 90 s of play) and the offline start hour (shared/clock.ts). */
export { CLOCK_HOURS_PER_SECOND, CLOCK_START_HOUR };

/**
 * The AUTO day as mood keyframes (hours). Between two keyframes the moods cross-fade (eased, see
 * `clockMood`); dawn borrows the dusk palette. The fades are long so the light never visibly jumps:
 * dawn 04:00–06:30 (night → dusk) and 06:30–09:00 (→ day), golden hour 16:00–18:30 (day → dusk), the
 * dusk itself holds 18:30–19:30 and darkens into night over three hours, 19:30–22:30. Day holds
 * 09:00–16:00, night 22:30–04:00. At 90 s an hour every fade lasts 3¾–4½ minutes of play.
 */
const DAY_KEYS: readonly [number, MoodId][] = [
  [4, 'night'],
  [6.5, 'dusk'],
  [9, 'overcast'],
  [16, 'overcast'],
  [18.5, 'dusk'],
  [19.5, 'dusk'],
  [22.5, 'night'],
];

/** Which two moods the clock is between at `hour`, and how far (0 = all `a`, 1 = all `b`). */
export function moodsAtHour(hour: number): { a: MoodId; b: MoodId; t: number } {
  const h = ((hour % 24) + 24) % 24;
  const n = DAY_KEYS.length;
  for (let i = 0; i < n; i++) {
    const [h0, a] = DAY_KEYS[i]!;
    const [h1raw, b] = DAY_KEYS[(i + 1) % n]!;
    const h1 = h1raw <= h0 ? h1raw + 24 : h1raw;
    const hh = h < h0 ? h + 24 : h;
    if (hh >= h0 && hh < h1) return { a, b, t: a === b ? 0 : (hh - h0) / (h1 - h0) };
  }
  return { a: 'night', b: 'night', t: 0 };
}

const smooth = (t: number): number => t * t * (3 - 2 * t);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerp3 = (a: readonly number[], b: readonly number[], t: number): [number, number, number] => [lerp(a[0]!, b[0]!, t), lerp(a[1]!, b[1]!, t), lerp(a[2]!, b[2]!, t)];
function lerpHex(a: number, b: number, t: number): number {
  const mix = (s: number): number => Math.round(lerp((a >> s) & 255, (b >> s) & 255, t)) << s;
  return mix(16) | mix(8) | mix(0);
}

/**
 * A mood part-way between two others. Everything numeric fades; what can't (the PMREM sky, built once per
 * mood id, the weather and the smoke colour) follows whichever side is nearer, so it changes once, mid-fade.
 */
export function blendMoods(a: Mood, b: Mood, t: number): Mood {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const near = t < 0.5 ? a : b;
  return {
    id: near.id,
    label: near.label,
    sun: { color: lerpHex(a.sun.color, b.sun.color, t), intensity: lerp(a.sun.intensity, b.sun.intensity, t), dir: lerp3(a.sun.dir, b.sun.dir, t) },
    hemi: { sky: lerpHex(a.hemi.sky, b.hemi.sky, t), ground: lerpHex(a.hemi.ground, b.hemi.ground, t), intensity: lerp(a.hemi.intensity, b.hemi.intensity, t) },
    rim: lerp(a.rim, b.rim, t),
    fog: { color: lerpHex(a.fog.color, b.fog.color, t), density: lerp(a.fog.density, b.fog.density, t) },
    exposure: lerp(a.exposure, b.exposure, t),
    envIntensity: lerp(a.envIntensity, b.envIntensity, t),
    windows: lerp(a.windows, b.windows, t),
    lamps: lerp(a.lamps, b.lamps, t),
    weather: near.weather,
    grade: {
      saturation: lerp(a.grade.saturation, b.grade.saturation, t),
      contrast: lerp(a.grade.contrast, b.grade.contrast, t),
      shadowTint: lerp3(a.grade.shadowTint, b.grade.shadowTint, t),
      highTint: lerp3(a.grade.highTint, b.grade.highTint, t),
    },
    bloom: lerp(a.bloom, b.bloom, t),
    bloomRadius: lerp(a.bloomRadius, b.bloomRadius, t),
    bloomThreshold: lerp(a.bloomThreshold, b.bloomThreshold, t),
    sky: near.sky,
    haze: { color: lerpHex(a.haze.color, b.haze.color, t), opacity: lerp(a.haze.opacity, b.haze.opacity, t) },
    dome: {
      zenith: lerpHex(a.dome.zenith, b.dome.zenith, t),
      glow: lerpHex(a.dome.glow, b.dome.glow, t),
      glowHigh: lerpHex(a.dome.glowHigh, b.dome.glowHigh, t),
      glowLevel: lerp(a.dome.glowLevel, b.dome.glowLevel, t),
      glowFocus: lerp(a.dome.glowFocus, b.dome.glowFocus, t),
      glowSpread: lerp(a.dome.glowSpread, b.dome.glowSpread, t),
      glowDir: [lerp(a.dome.glowDir[0], b.dome.glowDir[0], t), lerp(a.dome.glowDir[1], b.dome.glowDir[1], t)],
      cloud: lerp(a.dome.cloud, b.dome.cloud, t),
    },
  };
}

/**
 * A cross-fade moves in this many steps (one listener call each), not one per frame: about one every
 * 5 s of a clock fade, each far too small to see (exposure moves by < 0.005 a step).
 */
export const FADE_STEPS = 48;
const blendCache = new Map<string, Mood>();

/** The mood the borough clock shows at `hour` (cached per fade step, so equal steps are the same object). */
export function clockMood(hour: number): Mood {
  const { a, b, t } = moodsAtHour(hour);
  const q = Math.round(t * FADE_STEPS);
  if (q === 0) return MOODS[a];
  if (q === FADE_STEPS) return MOODS[b];
  const key = `${a}>${b}:${q}`;
  let m = blendCache.get(key);
  if (!m) {
    // Eased (smoothstep): a fade sets in and settles gently instead of starting and stopping on a kink.
    m = blendMoods(MOODS[a], MOODS[b], smooth(q / FADE_STEPS));
    blendCache.set(key, m);
  }
  return m;
}

const STORAGE_KEY = 'bh.timeOfDay';
/** Before the time-of-day setting the chosen mood itself was stored here; read once to migrate. */
const LEGACY_MOOD_KEY = 'bh.mood';
const listeners = new Set<(m: Mood) => void>();

const isTimeOfDay = (t: unknown): t is TimeOfDay => t === 'auto' || t === 'day' || t === 'dusk' || t === 'night';

function readTimeOfDay(): TimeOfDay {
  try {
    if (typeof localStorage === 'undefined') return 'dusk';
    const t = localStorage.getItem(STORAGE_KEY);
    if (isTimeOfDay(t)) return t;
    const legacy = localStorage.getItem(LEGACY_MOOD_KEY);
    if (legacy && Object.prototype.hasOwnProperty.call(MOODS, legacy)) return MOOD_TOD[legacy as MoodId];
  } catch {
    /* storage unavailable */
  }
  return 'dusk';
}

/**
 * Time-of-day state: the setting, the borough clock (hours, 0..24), a mission's forced mood, and online the
 * shard's weather (`shard` non-null: the shard owns the sky, so the clock shows whatever the setting).
 */
export const timeOfDayState: { setting: TimeOfDay; hour: number; forced: MoodId | null; shard: { weather: WeatherKind } | null } = {
  setting: readTimeOfDay(),
  hour: CLOCK_START_HOUR,
  forced: null,
  shard: null,
};

/** The setting in effect: online the shard's clock always shows (AUTO), offline the player's choice. */
export function effectiveTimeOfDay(): TimeOfDay {
  return timeOfDayState.shard ? 'auto' : timeOfDayState.setting;
}

/** `m` with another weather (cached per mood object, so an unchanged sky is the same object). */
const weatherCache = new WeakMap<Mood, Partial<Record<WeatherKind, Mood>>>();
function withWeather(m: Mood, w: WeatherKind): Mood {
  if (m.weather === w) return m;
  let byW = weatherCache.get(m);
  if (!byW) weatherCache.set(m, (byW = {}));
  return (byW[w] ??= { ...m, weather: w });
}

function resolveMood(): Mood {
  const s = timeOfDayState;
  const setting = effectiveTimeOfDay();
  const m = s.forced ? MOODS[s.forced] : setting === 'auto' ? clockMood(s.hour) : MOODS[TOD_MOOD[setting]];
  return s.shard ? withWeather(m, s.shard.weather) : m;
}

export const moodState: { current: Mood } = { current: resolveMood() };

/** The mood the setting, clock and any job ask for (what `getMood` reports), even while a switch fades to it. */
export function moodTarget(): Mood {
  return resolveMood();
}

/**
 * A switch of setting or a job's mood (DAY -> NIGHT, a night job starting) cross-fades over this many
 * seconds instead of cutting, in `SWITCH_STEPS` listener calls. Clock moves and debug hooks stay instant.
 */
export const SWITCH_SECONDS = 2.5;
const SWITCH_STEPS = 20;
/** The switch fade in progress: from the mood showing when it began, `t` 0..1. */
const switchFade: { from: Mood | null; t: number; step: number } = { from: null, t: 1, step: -1 };

/** Re-resolve; listeners only hear about it when the mood actually changed. */
function refresh(): void {
  let m = resolveMood();
  if (switchFade.from) {
    const q = Math.round(switchFade.t * SWITCH_STEPS);
    if (q >= SWITCH_STEPS) switchFade.from = null;
    else if (q === switchFade.step) return; // same step: nothing new to show (the target moves slowly)
    else {
      switchFade.step = q;
      m = blendMoods(switchFade.from, m, smooth(q / SWITCH_STEPS));
    }
  }
  if (m === moodState.current) return;
  moodState.current = m;
  for (const l of listeners) l(m);
}

/** Start a switch fade from the mood showing now (called before the state changes), or cancel one. */
function beginSwitch(fade: boolean | undefined): void {
  if (!fade) {
    switchFade.from = null;
    return;
  }
  switchFade.from = moodState.current;
  switchFade.t = 0;
  switchFade.step = -1;
}

/** Advance a switch fade (the time-of-day system, every frame where something is drawn). */
export function tickMoodSwitch(dt: number): void {
  if (!switchFade.from || dt <= 0) return;
  switchFade.t = Math.min(1, switchFade.t + dt / SWITCH_SECONDS);
  refresh();
}

/** Settings -> Time of day (persisted). `fade` eases the picture over instead of cutting. */
export function setTimeOfDay(t: TimeOfDay, opts?: { fade?: boolean }): void {
  if (!isTimeOfDay(t)) return;
  beginSwitch(opts?.fade && t !== timeOfDayState.setting);
  timeOfDayState.setting = t;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, t);
  } catch {
    /* ignore */
  }
  refresh();
}

/** Hold one mood: the matching time of day (overcast = DAY), persisted. What `__bh.setMood` does. */
export function setMood(id: MoodId): void {
  if (!Object.prototype.hasOwnProperty.call(MOODS, id)) return;
  setTimeOfDay(MOOD_TOD[id]);
}

/** A mission's mood for its duration (null hands back to the setting). Not persisted. */
export function forceMood(id: MoodId | null, opts?: { fade?: boolean }): void {
  const next = id && Object.prototype.hasOwnProperty.call(MOODS, id) ? id : null;
  beginSwitch(opts?.fade && next !== timeOfDayState.forced);
  timeOfDayState.forced = next;
  refresh();
}

/** Set the borough clock (hours; wraps). It only shows while the setting is AUTO and nothing is forced. */
export function setClockHour(hour: number): void {
  if (!Number.isFinite(hour)) return;
  timeOfDayState.hour = ((hour % 24) + 24) % 24;
  refresh();
}

/**
 * Online: follow the shard's sky (`null` hands back to the player's setting and the local clock). The hour
 * and weather come from the server; a big move of the clock (joining a shard at another hour) fades over in
 * SWITCH_SECONDS like a change of setting, small re-syncs just carry on, and a change of weather eases in
 * through the rain fx (fx/ambientFx.ts) without a lighting jump.
 */
/** A shard re-sync that moves the clock further than this (hours) cross-fades instead of carrying on. */
const SHARD_JUMP_HOURS = 0.25;
export function setShardSky(sky: { hour: number; weather: WeatherKind } | null, opts?: { fade?: boolean }): void {
  const s = timeOfDayState;
  if (sky && !Number.isFinite(sky.hour)) return;
  const next = sky ? ((sky.hour % 24) + 24) % 24 : s.hour;
  const d = (((next - s.hour) % 24) + 24) % 24;
  // Fade only when the picture would visibly jump: joining / leaving, or a re-sync far from where we were.
  const jump = (s.shard !== null) !== (sky !== null) || Math.min(d, 24 - d) > SHARD_JUMP_HOURS;
  // (A small re-sync leaves a fade already under way alone.)
  if (opts?.fade && jump) beginSwitch(true);
  else if (jump) beginSwitch(false);
  s.shard = sky ? { weather: sky.weather } : null;
  s.hour = next;
  refresh();
}

/** "21:40" */
export function formatClock(hour: number): string {
  const mins = Math.floor((((hour % 24) + 24) % 24) * 60);
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

/** Subscribe; the listener is called immediately with the current mood. Returns unsubscribe. */
export function onMood(fn: (m: Mood) => void): () => void {
  listeners.add(fn);
  fn(moodState.current);
  return () => listeners.delete(fn);
}

export interface AtmosphereService {
  readonly mood: MoodId;
  readonly moods: { id: MoodId; label: string }[];
  setMood(id: MoodId): void;
}

/** Settings → Time of day and the borough clock (provided by the time-of-day system, render/timeOfDay.ts). */
export interface TimeOfDayService {
  /** The setting in effect: the player's choice offline, always 'auto' online (the shard's clock). */
  readonly setting: TimeOfDay;
  /** Online: the shard owns the clock and the weather (the player's setting waits for offline play). */
  readonly shard: boolean;
  readonly options: readonly { id: TimeOfDay; label: string }[];
  /** Change the setting (in a rendered game the picture fades over in `SWITCH_SECONDS`). */
  set(t: TimeOfDay): void;
  /** Borough clock, hours 0..24 (it runs whatever the setting; it only shows on AUTO). */
  readonly hour: number;
  /** A mission's mood (null = none). */
  readonly forced: MoodId | null;
  /** Hold a job's mood (null hands back); fades over like `set`. */
  force(id: MoodId | null): void;
}

declare module '../core/services' {
  interface ServiceMap {
    /** Mood/weather selection (provided by render). */
    atmosphere: AtmosphereService;
    /** Time of day setting, borough clock and mission moods (provided by render/timeOfDay.ts). */
    timeOfDay: TimeOfDayService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** 'dusk' (drizzly dusk, default) | 'overcast' (overcast day) | 'night': holds that mood (Time of day DUSK / DAY / NIGHT, persisted). */
    setMood(id: string): void;
    getMood(): string;
    /** Settings → Time of day: 'auto' | 'day' | 'dusk' | 'night' (persisted). */
    setTimeOfDay(t: string): void;
    /** The setting in effect, the shard's sky online (null offline), the borough clock, the weather, the mood showing (a blend reports the nearer one) and any mission-forced mood. */
    getTimeOfDay(): { setting: string; shard: { weather: string } | null; hour: number; clock: string; mood: string; weather: string; forced: string | null; blend: { a: string; b: string; t: number } | null };
    /** Set the borough clock to `hour` (0..24). */
    setClockHour(hour: number): void;
  }
}
